// ═══════════════════════════════════════════════════════════════
// ChatAgentPanel.jsx — Agent-side support dashboard.
// Active chats, ticket queue, canned responses, status toggle,
// chat transfer, real-time updates
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PATHS, CHAT_STATUS, TICKET_STATUS, AGENT_STATUS, generateId } from './HelpCenterConfig.js';
import { resolveRecordScope, agentQuery, subscribeRecords } from './recordScope.js';

/**
 * Props:
 *   storage          — StorageAdapter instance
 *   pushAdapter      — PushAdapter instance
 *   presenceManager  — PresenceManager instance
 *   user             — { id, name, email, role }  (the support agent)
 *   config           — merged HelpCenterConfig
 */
function ChatAgentPanel({ storage, pushAdapter, presenceManager, user, config }) {
  const [agentStatus, setAgentStatus] = useState(AGENT_STATUS.OFFLINE);
  const [activeChats, setActiveChats] = useState([]);
  const [waitingChats, setWaitingChats] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState('chats'); // 'chats' | 'tickets'
  const [showCanned, setShowCanned] = useState(false);
  const [onlineAgents, setOnlineAgents] = useState([]);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const unsubChatsRef = useRef(null);
  const unsubTicketsRef = useRef(null);
  const unsubMsgsRef = useRef(null);
  const unsubPresenceRef = useRef(null);

  const primaryColor = config?.primaryColor || '#d97706';
  const maxLen = config?.maxMessageLength || 2000;
  const cannedResponses = config?.cannedResponses || [];

  // ── Go online on mount, offline on unmount ──
  useEffect(() => {
    if (!presenceManager || !user?.id) return;
    presenceManager.goOnline(user);
    setAgentStatus(AGENT_STATUS.ONLINE);

    return () => {
      presenceManager.goOffline(user);
    };
  }, [presenceManager, user?.id]);

  // ── Subscribe to other agents' presence ──
  useEffect(() => {
    if (!presenceManager) return;
    unsubPresenceRef.current = presenceManager.subscribeToPresence((agents) => {
      setOnlineAgents(agents.filter(a => a.id !== user?.id));
    });
    return () => { if (unsubPresenceRef.current) unsubPresenceRef.current(); };
  }, [presenceManager, user?.id]);

  // ── Subscribe to chats this agent may see (support: all, org admin: own org) ──
  useEffect(() => {
    if (!storage) return;
    let cancelled = false;
    resolveRecordScope(storage, user).then((scope) => {
      if (cancelled) return;
      unsubChatsRef.current = subscribeRecords(storage, PATHS.chatSessions(), agentQuery(scope), (data) => {
        if (!data) { setActiveChats([]); setWaitingChats([]); return; }
        const all = Object.entries(data).map(([key, val]) => ({ ...val, _key: key }));
        setWaitingChats(
          all.filter(c => c.status === CHAT_STATUS.WAITING)
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
        );
        setActiveChats(
          all.filter(c => c.status === CHAT_STATUS.ACTIVE && c.agentId === user?.id)
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
        );
      });
    });
    return () => { cancelled = true; if (unsubChatsRef.current) unsubChatsRef.current(); };
  }, [storage, user?.id]);

  // ── Subscribe to tickets this agent may see (support: all, org admin: own org) ──
  useEffect(() => {
    if (!storage) return;
    let cancelled = false;
    resolveRecordScope(storage, user).then((scope) => {
      if (cancelled) return;
      unsubTicketsRef.current = subscribeRecords(storage, PATHS.tickets(), agentQuery(scope), (data) => {
        if (!data) { setTickets([]); return; }
        const all = Object.entries(data)
          .map(([key, val]) => ({ ...val, _key: key }))
          .filter(t => t.status !== TICKET_STATUS.RESOLVED)
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        setTickets(all);
      });
    });
    return () => { cancelled = true; if (unsubTicketsRef.current) unsubTicketsRef.current(); };
  }, [storage, user?.id]);

  // ── Subscribe to selected chat/ticket messages ──
  useEffect(() => {
    if (!storage) return;
    if (unsubMsgsRef.current) unsubMsgsRef.current();

    if (selectedChat) {
      const chatId = selectedChat._key || selectedChat.id;
      unsubMsgsRef.current = storage.subscribe(PATHS.chatMessages(chatId), (data) => {
        if (!data) { setChatMessages([]); return; }
        const list = Object.entries(data)
          .map(([key, val]) => ({ ...val, _key: key }))
          .sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt));
        setChatMessages(list);

        // Mark unread user messages as read
        list.forEach(msg => {
          if (msg.senderId !== user?.id && !msg.readBy?.[user?.id]) {
            storage.update(`${PATHS.chatMessages(chatId)}/${msg._key}/readBy`, {
              [user.id]: new Date().toISOString(),
            }).catch(() => {});
          }
        });
      });
    } else if (selectedTicket) {
      const ticketId = selectedTicket._key || selectedTicket.id;
      unsubMsgsRef.current = storage.subscribe(PATHS.ticketMessages(ticketId), (data) => {
        if (!data) { setTicketMessages([]); return; }
        const list = Object.entries(data)
          .map(([key, val]) => ({ ...val, _key: key }))
          .sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt));
        setTicketMessages(list);
      });
    }

    return () => { if (unsubMsgsRef.current) unsubMsgsRef.current(); };
  }, [storage, selectedChat?._key || selectedChat?.id, selectedTicket?._key || selectedTicket?.id, user?.id]);

  // ── Auto-scroll ──
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages.length, ticketMessages.length]);

  // ── Toggle agent status ──
  const toggleStatus = () => {
    if (!presenceManager || !user) return;
    if (agentStatus === AGENT_STATUS.ONLINE) {
      presenceManager.goAway(user);
      setAgentStatus(AGENT_STATUS.AWAY);
    } else if (agentStatus === AGENT_STATUS.AWAY) {
      presenceManager.goOffline(user);
      setAgentStatus(AGENT_STATUS.OFFLINE);
    } else {
      presenceManager.goOnline(user);
      setAgentStatus(AGENT_STATUS.ONLINE);
    }
  };

  // ── Claim a waiting chat ──
  const claimChat = async (chat) => {
    if (!storage || !user?.id) return;
    const chatId = chat._key || chat.id;
    try {
      await storage.update(PATHS.chatSession(chatId), {
        status: CHAT_STATUS.ACTIVE,
        agentId: user.id,
        agentName: user.name || 'Support',
        updatedAt: new Date().toISOString(),
      });
      setSelectedChat({ ...chat, status: CHAT_STATUS.ACTIVE, agentId: user.id, agentName: user.name });
      setSelectedTicket(null);
      setTab('chats');

      // Notify user that agent joined
      if (pushAdapter && chat.userId) {
        pushAdapter.sendToUser(chat.userId, {
          title: 'Agent Connected',
          body: `${user.name || 'A support agent'} has joined your chat`,
          type: 'help-center-agent-joined',
          data: { chatId },
        }).catch(() => {});
      }
    } catch (e) {
      console.error('[ChatAgentPanel] Failed to claim chat:', e);
    }
  };

  // ── Claim a ticket ──
  const claimTicket = async (ticket) => {
    if (!storage || !user?.id) return;
    const ticketId = ticket._key || ticket.id;
    try {
      await storage.update(PATHS.ticket(ticketId), {
        status: TICKET_STATUS.IN_PROGRESS,
        assignedTo: user.id,
        assignedName: user.name || 'Support',
        updatedAt: new Date().toISOString(),
      });
      setSelectedTicket({ ...ticket, status: TICKET_STATUS.IN_PROGRESS, assignedTo: user.id, assignedName: user.name });
      setSelectedChat(null);
    } catch (e) {
      console.error('[ChatAgentPanel] Failed to claim ticket:', e);
    }
  };

  // ── Send message (chat or ticket) ──
  const sendMessage = async () => {
    if (!storage || !input.trim() || sending) return;
    if (input.trim().length > maxLen) return;
    setSending(true);

    const msgRecord = {
      id: generateId('msg'),
      senderId: user.id,
      senderName: user.name || 'Support',
      senderRole: 'agent',
      text: input.trim(),
      sentAt: new Date().toISOString(),
      readBy: { [user.id]: new Date().toISOString() },
    };

    try {
      if (selectedChat) {
        const chatId = selectedChat._key || selectedChat.id;
        await storage.push(PATHS.chatMessages(chatId), msgRecord);
        await storage.update(PATHS.chatSession(chatId), { updatedAt: new Date().toISOString() });

        // Typing indicator cleanup
        if (config?.enableTypingIndicators) {
          storage.remove(`${PATHS.chatTyping(chatId)}/${user.id}`).catch(() => {});
        }

        // Notify user
        if (pushAdapter && selectedChat.userId) {
          pushAdapter.sendToUser(selectedChat.userId, {
            title: 'Support',
            body: 'You have a new response from support.',
            type: 'help-center-chat-message',
            data: { chatId },
          }).catch(() => {});
        }
      } else if (selectedTicket) {
        const ticketId = selectedTicket._key || selectedTicket.id;
        await storage.push(PATHS.ticketMessages(ticketId), msgRecord);
        await storage.update(PATHS.ticket(ticketId), { updatedAt: new Date().toISOString() });

        // Notify user
        if (pushAdapter && selectedTicket.userId) {
          pushAdapter.sendToUser(selectedTicket.userId, {
            title: 'Support Response',
            body: 'You have a new reply on your support ticket.',
            type: 'help-center-ticket-reply',
            data: { ticketId },
          }).catch(() => {});
        }
      }

      setInput('');
      setShowCanned(false);
      if (inputRef.current) inputRef.current.focus();
    } catch (e) {
      console.error('[ChatAgentPanel] Failed to send message:', e);
    }
    setSending(false);
  };

  // ── Typing indicator for chat ──
  const handleInputChange = (e) => {
    const val = e.target.value;
    if (val.length <= maxLen) setInput(val);

    if (config?.enableTypingIndicators && selectedChat && storage) {
      const chatId = selectedChat._key || selectedChat.id;
      storage.set(`${PATHS.chatTyping(chatId)}/${user.id}`, {
        timestamp: new Date().toISOString(),
      }).catch(() => {});
    }
  };

  // ── End a chat ──
  const endChat = async () => {
    if (!storage || !selectedChat) return;
    const chatId = selectedChat._key || selectedChat.id;
    try {
      await storage.update(PATHS.chatSession(chatId), {
        status: CHAT_STATUS.ENDED,
        endedAt: new Date().toISOString(),
        endedBy: user.id,
      });
      setSelectedChat(null);
      setChatMessages([]);
    } catch (e) {
      console.error('[ChatAgentPanel] Failed to end chat:', e);
    }
  };

  // ── Resolve a ticket ──
  const resolveTicket = async () => {
    if (!storage || !selectedTicket) return;
    const ticketId = selectedTicket._key || selectedTicket.id;
    try {
      await storage.update(PATHS.ticket(ticketId), {
        status: TICKET_STATUS.RESOLVED,
        resolvedAt: new Date().toISOString(),
        resolvedBy: user.id,
        updatedAt: new Date().toISOString(),
      });

      if (pushAdapter && selectedTicket.userId) {
        pushAdapter.sendToUser(selectedTicket.userId, {
          title: 'Ticket Resolved',
          body: `Your support ticket "${selectedTicket.subject || 'Support Request'}" has been resolved`,
          type: 'help-center-ticket-resolved',
          data: { ticketId },
        }).catch(() => {});
      }

      setSelectedTicket(null);
      setTicketMessages([]);
    } catch (e) {
      console.error('[ChatAgentPanel] Failed to resolve ticket:', e);
    }
  };

  // ── Transfer chat to another agent ──
  const transferChat = async (toAgent) => {
    if (!storage || !selectedChat) return;
    const chatId = selectedChat._key || selectedChat.id;
    try {
      await storage.update(PATHS.chatSession(chatId), {
        agentId: toAgent.id,
        agentName: toAgent.name,
        updatedAt: new Date().toISOString(),
      });

      // System message
      await storage.push(PATHS.chatMessages(chatId), {
        id: generateId('msg'),
        senderId: 'system',
        senderName: 'System',
        senderRole: 'system',
        text: `Chat transferred from ${user.name || 'agent'} to ${toAgent.name || 'another agent'}.`,
        sentAt: new Date().toISOString(),
      });

      if (pushAdapter) {
        pushAdapter.sendToUser(toAgent.id, {
          title: 'Chat Transferred',
          body: `${user.name || 'An agent'} transferred a chat to you`,
          type: 'help-center-chat-transfer',
          data: { chatId },
        }).catch(() => {});
      }

      setSelectedChat(null);
      setChatMessages([]);
    } catch (e) {
      console.error('[ChatAgentPanel] Failed to transfer chat:', e);
    }
  };

  // ── Convert chat to ticket ──
  const convertChatToTicket = async (chat) => {
    if (!storage || !user?.id) return;
    const chatId = chat._key || chat.id;
    try {
      // Load chat messages for context
      const msgData = await storage.get(PATHS.chatMessages(chatId));
      const firstMsg = msgData
        ? Object.values(msgData).sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt))[0]
        : null;

      const ticketId = generateId('ticket');
      const ticketRecord = {
        id: ticketId,
        userId: chat.userId,
        tenantId: chat.tenantId || null,
        userName: chat.userName || 'User',
        userEmail: chat.userEmail || null,
        subject: `Follow-up from chat (${new Date(chat.createdAt).toLocaleDateString()})`,
        status: TICKET_STATUS.OPEN,
        context: chat.context || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        assignedTo: user.id,
        assignedName: user.name || 'Support',
        linkedChatId: chatId,
      };

      await storage.set(PATHS.ticket(ticketId), ticketRecord);

      // Copy first user message as the ticket's initial message
      if (firstMsg) {
        await storage.push(PATHS.ticketMessages(ticketId), {
          id: generateId('tmsg'),
          senderId: firstMsg.senderId,
          senderName: firstMsg.senderName || 'User',
          senderRole: firstMsg.senderRole || 'user',
          text: firstMsg.text,
          sentAt: firstMsg.sentAt,
        });
      }

      // Notify user
      if (pushAdapter && chat.userId) {
        pushAdapter.sendToUser(chat.userId, {
          title: 'Support Ticket Created',
          body: 'A support ticket has been created from your chat for follow-up.',
          type: 'help-center-ticket',
          data: { ticketId },
        }).catch(() => {});
      }

      // Switch to view the new ticket
      setSelectedChat(null);
      setChatMessages([]);
      setSelectedTicket({ ...ticketRecord, _key: ticketId });
      setTab('tickets');
    } catch (e) {
      console.error('[ChatAgentPanel] Failed to convert chat to ticket:', e);
    }
  };

  // ── Back to list (mobile) ──
  const backToList = () => {
    setSelectedChat(null);
    setSelectedTicket(null);
    setChatMessages([]);
    setTicketMessages([]);
  };

  // ── Insert canned response ──
  const insertCanned = (text) => {
    setInput(text);
    setShowCanned(false);
    if (inputRef.current) inputRef.current.focus();
  };

  // ── Key handler ──
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── Icons ──
  const StatusDot = ({ status }) => {
    const colors = { online: 'bg-green-500', away: 'bg-amber-500', offline: 'bg-stone-300' };
    return <span className={`w-2.5 h-2.5 rounded-full ${colors[status] || colors.offline}`} />;
  };

  const statusLabels = { online: 'Online', away: 'Away', offline: 'Offline' };

  // ── Render ──
  const currentMessages = selectedChat ? chatMessages : ticketMessages;
  const isConversationActive = selectedChat || selectedTicket;

  return (
    <div className="flex flex-col h-full" style={{ minHeight: '500px' }}>
      {/* Agent Header */}
      <div className="flex items-center justify-between p-3 border-b border-stone-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: primaryColor }}>
            {(user?.name || 'A')[0].toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-medium text-stone-800">{user?.name || 'Agent'}</p>
            <div className="flex items-center gap-1.5">
              <StatusDot status={agentStatus} />
              <span className="text-xs text-stone-500">{statusLabels[agentStatus]}</span>
            </div>
          </div>
        </div>

        <button
          onClick={toggleStatus}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            agentStatus === AGENT_STATUS.ONLINE
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : agentStatus === AGENT_STATUS.AWAY
              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
          }`}
        >
          {agentStatus === AGENT_STATUS.ONLINE ? 'Go Away' : agentStatus === AGENT_STATUS.AWAY ? 'Go Offline' : 'Go Online'}
        </button>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Show list when no conversation is selected */}
        {!isConversationActive ? (
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-stone-200 flex-shrink-0">
              <button
                onClick={() => setTab('chats')}
                className={`flex-1 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                  tab === 'chats' ? 'border-b-2 text-stone-800' : 'text-stone-500 hover:text-stone-600'
                }`}
                style={tab === 'chats' ? { borderBottomColor: primaryColor } : undefined}
              >
                Chats
                {(waitingChats.length + activeChats.length) > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-red-600 text-white text-[10px] rounded-full">
                    {waitingChats.length + activeChats.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setTab('tickets')}
                className={`flex-1 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                  tab === 'tickets' ? 'border-b-2 text-stone-800' : 'text-stone-500 hover:text-stone-600'
                }`}
                style={tab === 'tickets' ? { borderBottomColor: primaryColor } : undefined}
              >
                Tickets
                {tickets.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-amber-500 text-stone-900 text-[10px] rounded-full">
                    {tickets.length}
                  </span>
                )}
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto">
              {tab === 'chats' && (
                <div>
                  {/* Waiting */}
                  {waitingChats.length > 0 && (
                    <div className="p-2">
                      <p className="text-[10px] font-semibold text-red-700 uppercase tracking-wider px-2 mb-1">
                        Waiting ({waitingChats.length})
                      </p>
                      {waitingChats.map(chat => (
                        <button
                          key={chat._key || chat.id}
                          onClick={() => claimChat(chat)}
                          className="w-full p-2 mb-1 rounded-lg bg-red-50 hover:bg-red-100 text-left transition-colors"
                        >
                          <p className="text-sm text-stone-800 font-medium truncate">{chat.userName || 'User'}</p>
                          <p className="text-xs text-stone-500 truncate">{chat.userEmail || 'Tap to claim'}</p>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Active */}
                  {activeChats.length > 0 && (
                    <div className="p-2">
                      <p className="text-[10px] font-semibold text-green-700 uppercase tracking-wider px-2 mb-1">
                        Active ({activeChats.length})
                      </p>
                      {activeChats.map(chat => (
                        <button
                          key={chat._key || chat.id}
                          onClick={() => { setSelectedChat(chat); setSelectedTicket(null); }}
                          className="w-full p-2 mb-1 rounded-lg text-left transition-colors hover:bg-stone-100"
                        >
                          <p className="text-sm text-stone-800 font-medium truncate">{chat.userName || 'User'}</p>
                          <p className="text-xs text-stone-500 truncate">
                            {new Date(chat.updatedAt || chat.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}

                  {waitingChats.length === 0 && activeChats.length === 0 && (
                    <p className="text-xs text-stone-500 text-center py-8">No active chats</p>
                  )}
                </div>
              )}

              {tab === 'tickets' && (
                <div className="p-2">
                  {tickets.length === 0 && (
                    <p className="text-xs text-stone-500 text-center py-8">No open tickets</p>
                  )}
                  {tickets.map(ticket => {
                    const isAssigned = ticket.assignedTo === user?.id;
                    return (
                      <button
                        key={ticket._key || ticket.id}
                        onClick={() => { isAssigned ? setSelectedTicket(ticket) : claimTicket(ticket); setSelectedChat(null); }}
                        className={`w-full p-2 mb-1 rounded-lg text-left transition-colors ${
                          isAssigned ? 'hover:bg-stone-100' : 'bg-amber-50 hover:bg-amber-100'
                        }`}
                      >
                        <p className="text-sm text-stone-800 font-medium truncate">{ticket.subject || 'Support Request'}</p>
                        <p className="text-xs text-stone-500 truncate">
                          {ticket.userName} · {isAssigned ? 'Assigned to you' : 'Unassigned'}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}

              {onlineAgents.length > 0 && (
                <p className="text-xs text-stone-300 text-center py-2">
                  {onlineAgents.length} other agent(s) online
                </p>
              )}
            </div>
          </div>
        ) : (
          /* Conversation view — full width */
          <div className="flex-1 flex flex-col overflow-hidden">
              {/* Conversation header */}
              <div className="flex items-center gap-2 p-3 border-b border-stone-200 flex-shrink-0">
                <button
                  onClick={backToList}
                  className="p-1 hover:bg-stone-100 rounded-lg flex-shrink-0"
                  aria-label="Back to conversations"
                >
                  <svg aria-hidden="true" className="w-5 h-5 text-stone-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path d="m15 18-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-800 truncate">
                    {selectedChat?.userName || selectedTicket?.userName || 'User'}
                  </p>
                  <p className="text-xs text-stone-500 truncate">
                    {selectedChat?.userEmail || selectedTicket?.userEmail || ''}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end">
                  {/* Transfer (chat only) */}
                  {selectedChat && onlineAgents.length > 0 && (
                    <select
                      onChange={(e) => {
                        const agent = onlineAgents.find(a => a.id === e.target.value);
                        if (agent) transferChat(agent);
                        e.target.value = '';
                      }}
                      defaultValue=""
                      className="text-xs border border-stone-300 rounded-lg px-1.5 py-1 text-stone-600 bg-white max-w-[100px]"
                    >
                      <option value="" disabled>Transfer...</option>
                      {onlineAgents.map(a => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  )}

                  {/* Convert to ticket (chat only) */}
                  {selectedChat && (
                    <button
                      onClick={() => convertChatToTicket(selectedChat)}
                      className="text-xs text-stone-500 hover:text-amber-700 px-1.5 py-1 rounded hover:bg-amber-50 transition-colors"
                      title="Convert to ticket"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 0 0-2 2v3a2 2 0 1 1 0 4v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 1 1 0-4V7a2 2 0 0 0-2-2H5z" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  )}

                  {/* End / Resolve */}
                  {selectedChat && (
                    <button
                      onClick={endChat}
                      className="text-xs text-stone-500 hover:text-red-600 px-1.5 py-1 rounded hover:bg-red-50 transition-colors"
                    >
                      End
                    </button>
                  )}
                  {selectedTicket && selectedTicket.status !== TICKET_STATUS.RESOLVED && (
                    <button
                      onClick={resolveTicket}
                      className="text-xs px-2 py-1 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg font-medium transition-colors"
                    >
                      Resolve
                    </button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {currentMessages.map((msg) => {
                  const isOwn = msg.senderId === user?.id;
                  const isSystem = msg.senderRole === 'system';
                  const time = new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                  if (isSystem) {
                    return (
                      <div key={msg._key || msg.id} className="text-center">
                        <span className="text-xs text-stone-600 bg-stone-100 px-3 py-1 rounded-full">
                          {msg.text}
                        </span>
                      </div>
                    );
                  }

                  return (
                    <div key={msg._key || msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'} items-end gap-2`}>
                      {!isOwn && (
                        <div className="w-7 h-7 rounded-full bg-stone-200 flex items-center justify-center text-stone-600 text-xs font-bold flex-shrink-0">
                          {(msg.senderName || 'U')[0].toUpperCase()}
                        </div>
                      )}
                      <div className="max-w-[75%]">
                        {!isOwn && (
                          <p className="text-xs text-stone-500 mb-0.5 ml-1">{msg.senderName || 'User'}</p>
                        )}
                        <div
                          className={`px-3.5 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                            isOwn
                              ? 'rounded-br-sm text-white'
                              : 'rounded-bl-sm bg-stone-100 text-stone-800'
                          }`}
                          style={isOwn ? { backgroundColor: primaryColor } : undefined}
                        >
                          {msg.text}
                        </div>
                        <p className={`text-[10px] text-stone-500 mt-0.5 ${isOwn ? 'text-right' : 'text-left'} px-1`}>
                          {time}
                          {isOwn && msg.readBy && Object.keys(msg.readBy).length > 1 && (
                            <span className="text-blue-600 ml-1">✓✓</span>
                          )}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Compose */}
              <div className="border-t border-stone-200 flex-shrink-0">
                {/* Canned Responses */}
                {showCanned && cannedResponses.length > 0 && (
                  <div className="p-2 border-b border-stone-100 bg-stone-50 max-h-40 overflow-y-auto">
                    <p className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider px-2 mb-1">Canned Responses</p>
                    {cannedResponses.map(cr => (
                      <button
                        key={cr.id}
                        onClick={() => insertCanned(cr.text)}
                        className="w-full text-left p-2 rounded-lg hover:bg-stone-100 transition-colors"
                      >
                        <p className="text-xs font-medium text-stone-700">{cr.label}</p>
                        <p className="text-xs text-stone-500 truncate">{cr.text}</p>
                      </button>
                    ))}
                  </div>
                )}

                <div className="p-3">
                  <div className="flex items-end gap-2">
                    {cannedResponses.length > 0 && (
                      <button
                        onClick={() => setShowCanned(!showCanned)}
                        className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                          showCanned ? 'bg-stone-200 text-stone-700' : 'text-stone-600 hover:bg-stone-100'
                        }`}
                        title="Canned responses"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M17 21v-8H7v8M7 3v5h8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    )}
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      placeholder="Type a reply..."
                      rows={1}
                      className="flex-1 p-2.5 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent resize-none text-stone-700 placeholder-stone-500 text-sm"
                      style={{ '--tw-ring-color': primaryColor, maxHeight: '120px' }}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!input.trim() || sending}
                      className="p-2.5 text-white rounded-xl transition-colors disabled:opacity-50 hover:opacity-90 flex-shrink-0"
                      style={{ backgroundColor: primaryColor }}
                      aria-label="Send message"
                    >
                      <svg aria-hidden="true" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path d="m22 2-7 20-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatAgentPanel;
